import React from "react";

import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import { moneyFromMajorFloat, moneyToWire, toMinorUnits, zeroMoney } from "@performyard/money";
import { ButtonIcon } from "@performyard/py-components";
import type { ICellRendererParams } from "@performyard/py-components";
import type { BudgetDecisionData } from "@performyard/service-contracts";

import type { DecisionRow } from "../../types";
import { DecisionTableContext } from "../MyDecisions/types";

import { useCycleOwnerEditBudgetDecision } from "../../../../api/hooks";

import { coerceBudgetIncreaseNumber } from "./utils";

import { useIncreaseValue, useIsEditingRow } from "../MyDecisions/decision-store";

export const BudgetReviewRowEditCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;

	const context = params.context as DecisionTableContext;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	const storeIsEditing = useIsEditingRow(context?.store, row?._id ?? "");
	const isEditingThisRow = context?.store
		? Boolean(row && !row.superRow && storeIsEditing)
		: Boolean(row && !row.superRow && context.editingRowIds?.has(row._id));
	const newEditId = row?._id ?? "";
	const cycleId = row?.compensation_cycle_id || "";
	const increaseVal = storeValue ?? context.increaseValues[newEditId];
	// Whether the edit is a real change gates the Save button, so it is compared on
	// exact minor units rather than two separately-rounded floats.
	const noChange =
		toMinorUnits(row?.proposed_increase_amount ?? zeroMoney()) ===
		toMinorUnits(moneyFromMajorFloat(coerceBudgetIncreaseNumber(increaseVal?.amount)));
	const canEdit = Boolean(cycleId && newEditId && row && !noChange);
	// The edited amount is a user-typed major-unit number and becomes exact money here.
	// The current salary is already `Money` on the row, so it serializes directly —
	// there is no rounded copy to accidentally send instead.
	const budgetDecisionData: BudgetDecisionData =
		row && canEdit
			? {
					employeeBeingUpdated: row.employee_id,
					increasePercent: coerceBudgetIncreaseNumber(increaseVal?.percent),
					increaseAmount: moneyToWire(moneyFromMajorFloat(coerceBudgetIncreaseNumber(increaseVal?.amount))),
					currentSalary: moneyToWire(row.current_salary),
				}
			: {
					employeeBeingUpdated: "",
					increasePercent: 0,
					increaseAmount: moneyToWire(zeroMoney()),
					currentSalary: moneyToWire(zeroMoney()),
				};
	const { mutate: editBudgetDecisionRow } = useCycleOwnerEditBudgetDecision(cycleId, budgetDecisionData);

	if (!row || row.superRow) {
		return null;
	}
	const handleEdit = () => {
		if (canEdit) {
			editBudgetDecisionRow();
		} else {
			context.onBudgetReviewCancelEditRow?.(row);
		}
	};
	return (
		<div className="flex h-full items-center justify-end pr-1">
			{!isEditingThisRow && (
				<ButtonIcon
					variant="standard"
					color="neutral"
					shade={3}
					size={20}
					icon={faPencil}
					onClick={() => {
						context.onBudgetReviewEditRow?.(row);
					}}
					aria-label={`Edit ${row.employee_name}`}
				/>
			)}
			{isEditingThisRow && (
				<div className="inline-flex shrink-0 flex-row items-center gap-[2px]">
					<ButtonIcon
						variant="standard"
						color="green"
						shade={5}
						size={20}
						icon={faCircleCheck}
						onClick={handleEdit}
						aria-label={`Edit ${row.employee_name}`}
					/>
					<ButtonIcon
						variant="standard"
						color="red"
						shade={5}
						size={20}
						icon={faCircleXmark}
						onClick={() => {
							context.onBudgetReviewCancelEditRow?.(row);
						}}
						aria-label={`Edit ${row.employee_name}`}
					/>
				</div>
			)}
		</div>
	);
};
